import React, { useContext, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { css } from '@emotion/react';
import * as Sentry from '@sentry/react';
import { SectionSubtitle, GreyBody, PrimaryButton } from '@/styles/blocks';
import { CorpAdminContext } from '../../store';
import { GET_CORP_INTEGRATION_OVERRIDES, INSERT_CORP_INTEGRATION_OVERRIDE } from '../../graphql';
import IntegrationRow from '../IntegrationRow';

// PC Miler mileage setup for a shipper. The override is carrier-less (shipper-scoped); it's
// read from the store's already-loaded overrides so IntegrationRow's save-path cache updates
// stay in sync here. When absent, bootstrap it directly as type PC_MILER (skips MANUAL).
const MileageIntegrations = ({ clientId }) => {
    const { state, callbacks } = useContext(CorpAdminContext);
    const { setNotification } = callbacks;
    const { orgShipperIds } = state;

    const override = useMemo(
        () =>
            (state.integrationOverrides || []).find(
                (o) => o.type === 'PC_MILER' && o.shipper_id === clientId && !o.carrier_id
            ),
        [state.integrationOverrides, clientId]
    );

    const [insertOverride, { loading }] = useMutation(INSERT_CORP_INTEGRATION_OVERRIDE, {
        update(cache, { data }) {
            const created = data?.insert_integration_overrides_one;
            if (!created) return;
            try {
                const existing = cache.readQuery({
                    query: GET_CORP_INTEGRATION_OVERRIDES,
                    variables: { orgShipperIds },
                });
                if (!existing) return;
                cache.writeQuery({
                    query: GET_CORP_INTEGRATION_OVERRIDES,
                    variables: { orgShipperIds },
                    data: { integration_overrides: [...existing.integration_overrides, created] },
                });
            } catch {
                // cache miss is non-fatal
            }
        },
    });

    const handleSetup = async () => {
        try {
            await insertOverride({
                variables: {
                    object: { carrier_id: null, shipper_id: clientId, client_id: clientId, type: 'PC_MILER' },
                },
            });
        } catch (err) {
            Sentry.captureException(err);
            setNotification?.({ severity: 'error', message: 'Failed to start PC Miler setup' });
        }
    };

    return (
        <div>
            <SectionSubtitle
                css={css`
                    display: block;
                    margin-bottom: 0.5rem;
                `}
            >
                PC Miler
            </SectionSubtitle>
            <GreyBody
                css={css`
                    display: block;
                    margin-bottom: 1rem;
                `}
            >
                When enabled, this shipper's order mileage is calculated with PC Miler instead of Google. The version
                used is pinned to each order on its first calculation.
            </GreyBody>
            {override ? (
                <IntegrationRow integration={override} />
            ) : (
                <PrimaryButton loading={loading} onClick={handleSetup}>
                    Set up PC Miler
                </PrimaryButton>
            )}
        </div>
    );
};

export default MileageIntegrations;
